import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Frame-accurate control over a <video>.
 *
 * Browsers only expose time, not frames, so everything here is a conversion:
 *  - a frame N occupies [N/fps, (N+1)/fps), so the frame shown at time t is floor(t*fps)
 *  - to *land on* frame N we seek to its midpoint, (N+0.5)/fps, which keeps a rounding error
 *    of half a frame from putting us on the neighbour
 *
 * `requestVideoFrameCallback` reports the presentation time of the frame actually on screen,
 * which is the only trustworthy source for "what am I looking at right now".
 */

const EPSILON = 1e-4;

export interface FramePlayer {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  frame: number;
  playing: boolean;
  rate: number;
  /** True once metadata is loaded and seeking is meaningful. */
  ready: boolean;
  buffered: number;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seekFrame: (frame: number) => void;
  stepFrames: (delta: number) => void;
  setRate: (rate: number) => void;
  /** Current media time in seconds, read straight off the element. */
  currentTime: () => number;
}

export function frameToTime(frame: number, fps: number): number {
  return (frame + 0.5) / fps;
}

export function timeToFrame(time: number, fps: number, frameCount: number): number {
  const raw = Math.floor(time * fps + EPSILON);
  return Math.max(0, Math.min(frameCount > 0 ? frameCount - 1 : raw, raw));
}

/**
 * `videoKey` names the current video element. It has to change whenever `<video key=...>` does,
 * or the effects below re-attach their event listeners to the *previous* element — visible as
 * a frozen timecode and dead play button after a version switch when two versions share fps
 * and frameCount (the very common case).
 */
export function useFramePlayer(fps: number, frameCount: number, videoKey?: string): FramePlayer {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [frame, setFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRateState] = useState(1);
  const [ready, setReady] = useState(false);
  const [buffered, setBuffered] = useState(0);

  // Track the displayed frame via rVFC, falling back to a rAF poll on browsers without it.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    let rvfcHandle = 0;
    let rafHandle = 0;

    const hasRvfc = typeof video.requestVideoFrameCallback === 'function';

    const onFrame = (_now: number, meta: { mediaTime: number }): void => {
      if (cancelled) return;
      setFrame(timeToFrame(meta.mediaTime, fps, frameCount));
      rvfcHandle = video.requestVideoFrameCallback(onFrame);
    };

    const poll = (): void => {
      if (cancelled) return;
      setFrame(timeToFrame(video.currentTime, fps, frameCount));
      rafHandle = requestAnimationFrame(poll);
    };

    if (hasRvfc) rvfcHandle = video.requestVideoFrameCallback(onFrame);
    else rafHandle = requestAnimationFrame(poll);

    return () => {
      cancelled = true;
      if (hasRvfc && rvfcHandle) video.cancelVideoFrameCallback?.(rvfcHandle);
      if (rafHandle) cancelAnimationFrame(rafHandle);
    };
  }, [fps, frameCount, videoKey]);

  // Element state -> React state.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // A fresh <video> (new videoKey) always starts paused but never fires a 'pause' event to
    // say so, since it was never playing. Sync explicitly or the button keeps showing the
    // previous clip's state.
    setPlaying(!video.paused);

    const onPlay = (): void => setPlaying(true);
    const onPause = (): void => setPlaying(false);
    const onLoaded = (): void => {
      setReady(true);
      setFrame(timeToFrame(video.currentTime, fps, frameCount));
    };
    const onRate = (): void => setRateState(video.playbackRate);
    const onSeeked = (): void => setFrame(timeToFrame(video.currentTime, fps, frameCount));
    const onProgress = (): void => {
      if (video.buffered.length > 0 && video.duration > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1) / video.duration);
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('ratechange', onRate);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('progress', onProgress);
    video.addEventListener('timeupdate', onProgress);

    if (video.readyState >= 1) onLoaded();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('ratechange', onRate);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('timeupdate', onProgress);
    };
  }, [fps, frameCount, videoKey]);

  const play = useCallback(() => {
    // A rejected play() (autoplay policy) must not leave the UI claiming it is playing.
    void videoRef.current?.play().catch(() => setPlaying(false));
  }, []);

  const pause = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => setPlaying(false));
    else video.pause();
  }, []);

  const seekFrame = useCallback(
    (target: number) => {
      const video = videoRef.current;
      if (!video) return;
      const clamped = Math.max(0, Math.min(frameCount > 0 ? frameCount - 1 : target, Math.round(target)));
      video.currentTime = frameToTime(clamped, fps);
      setFrame(clamped);
    },
    [fps, frameCount],
  );

  const stepFrames = useCallback(
    (delta: number) => {
      const video = videoRef.current;
      if (!video) return;
      // Stepping implies inspecting a still, so stop playback first.
      if (!video.paused) video.pause();
      const current = timeToFrame(video.currentTime, fps, frameCount);
      seekFrame(current + delta);
    },
    [fps, frameCount, seekFrame],
  );

  const setRate = useCallback((next: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = next;
    setRateState(next);
  }, []);

  const currentTime = useCallback(() => videoRef.current?.currentTime ?? 0, []);

  return {
    videoRef,
    frame,
    playing,
    rate,
    ready,
    buffered,
    play,
    pause,
    togglePlay,
    seekFrame,
    stepFrames,
    setRate,
    currentTime,
  };
}

/**
 * Locks a second video to a master video. Used by the wipe/compare modes, where two clips
 * must show the same frame or the comparison is a lie.
 *
 * Small offsets are corrected by nudging playbackRate (invisible); large ones by seeking.
 */
export function useFollowerVideo(
  masterRef: React.RefObject<HTMLVideoElement | null>,
  followerRef: React.RefObject<HTMLVideoElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;

    const HARD_SEEK = 0.25; // seconds — beyond this, nudging would take too long
    const NUDGE = 0.012; // ~a third of a frame at 25 fps

    let raf = 0;
    const tick = (): void => {
      // Read live: the master element is swapped out from under us on every clip change (the
      // player is double-buffered), so capturing it once would leave this driving a dead video.
      const master = masterRef.current;
      const follower = followerRef.current;
      if (!master || !follower) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const drift = master.currentTime - follower.currentTime;

      if (Math.abs(drift) > HARD_SEEK) {
        follower.currentTime = master.currentTime;
        follower.playbackRate = master.playbackRate;
      } else if (Math.abs(drift) > NUDGE && !master.paused) {
        // Run the follower slightly fast or slow until it catches up.
        follower.playbackRate = master.playbackRate * (drift > 0 ? 1.05 : 0.95);
      } else {
        follower.playbackRate = master.playbackRate;
      }

      if (master.paused && !follower.paused) follower.pause();
      if (!master.paused && follower.paused) void follower.play().catch(() => undefined);
      // While paused, keep the follower exactly on the master's frame.
      if (master.paused && Math.abs(drift) > 0.001) follower.currentTime = master.currentTime;

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [masterRef, followerRef, enabled]);
}
